import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faPen } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LineupHeader as DumbLineupHeader } from '@sorare/core/src/components/lineup/LineupHeader';
import { useSportContext } from '@sorare/core/src/contexts/sport';

import { Badge } from 'components/managerTeam/Badge';
import { getLineupDisplayName } from 'lib/lineup/getLineupDisplayName';
import { isFixtureStarted } from 'lib/so5';

import { ManagerTeamUpdateDialog } from '../LineupButtons/ManagerTeamUpdateDialog';
import type { LobbyLineupHeader_so5LeaderboardContender } from './__generated__/index.graphql';

const Root = styled(Horizontal)`
  width: 100%;
`;

const SmallIcon = styled.img`
  width: 14px;
  height: 14px;
  object-fit: contain;
`;

type Props = {
  so5LeaderboardContender: Nullable<LobbyLineupHeader_so5LeaderboardContender>;
  showLineupsCount?: boolean;
  showRenameButton?: boolean;
  small?: boolean;
};

export const LineupHeader = ({
  so5LeaderboardContender,
  showLineupsCount,
  showRenameButton,
  small,
}: Props) => {
  const { sport } = useSportContext();
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);

  const { so5Leaderboard, so5Lineup, managerTeam } =
    so5LeaderboardContender || {};
  const { so5Fixture } = so5Leaderboard || {};

  const { so5Rankings, hasLiveGames } = so5Lineup || {};
  const ranking = so5Rankings?.[0];
  const fixtureStarted = so5Fixture && isFixtureStarted(so5Fixture);

  const leagueName =
    so5Leaderboard?.so5TournamentType.so5LeaderboardType.includes('GLOBAL_CAP')
      ? so5Leaderboard?.displayName
      : so5Leaderboard?.so5LeaderboardGroupByCompetition.displayName;

  if (!so5LeaderboardContender) {
    return <Root />;
  }

  return (
    <DumbLineupHeader
      small={small}
      leaderboardName={
        getLineupDisplayName({
          so5Leaderboard,
          so5Lineup,
          managerTeam,
          sport,
        }) || ''
      }
      action={
        !!managerTeam &&
        !!showRenameButton && (
          <>
            <ButtonBase onClick={() => setRenameDialogOpen(true)}>
              <FontAwesomeIcon icon={faPen} width={12} height={12} />
            </ButtonBase>
            <ManagerTeamUpdateDialog
              open={renameDialogOpen}
              onClose={() => setRenameDialogOpen(false)}
              managerTeam={managerTeam}
            />
          </>
        )
      }
      extra={
        !!ranking?.targetedRewardWithEligibility && (
          <img
            src={
              ranking?.targetedRewardWithEligibility.reward.target?.pictureUrl
            }
            alt=""
            width="12"
            height="12"
          />
        )
      }
      badge={
        <Badge
          size={small ? 4 : undefined}
          hasLiveGames={!!hasLiveGames}
          managerTeam={managerTeam}
          ranking={fixtureStarted ? ranking : undefined}
          leaderboard={so5Leaderboard}
        />
      }
      label={
        [Sport.NBA, Sport.FOOTBALL].includesValue(sport) && (
          <Horizontal gap={0.5}>
            <SmallIcon
              src={so5Leaderboard?.so5LeaderboardGroupByCompetition?.iconUrl}
              alt=""
            />
            {so5Leaderboard?.roomWithBoostedRewards ? (
              <span
                className="text-gradient-color"
                style={
                  {
                    '--text-gradient-color': 'var(--c-gradient-gold)',
                  } as CSSProperties
                }
              >{`Golden ${leagueName}`}</span>
            ) : (
              <span>{leagueName}</span>
            )}
          </Horizontal>
        )
      }
      showLiveDot={!!showLineupsCount && sport === Sport.FOOTBALL}
      lineupCount={
        showLineupsCount ? so5Leaderboard?.so5LineupsCount || 0 : undefined
      }
    />
  );
};

LineupHeader.fragments = {
  so5LeaderboardContender: gql`
    fragment LobbyLineupHeader_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Leaderboard {
        slug
        shortDisplayName: displayName(short: true)
        seasonality
        seasonalityName
        roomWithBoostedRewards
        teamsCap
        so5LineupsCount
        so5TournamentType {
          id
          so5LeaderboardType
        }
        so5Fixture {
          slug
          ...isFixtureStarted_so5Fixture
        }
        so5LeaderboardGroupByCompetition: so5LeaderboardGroup(
          groupType: COMPETITION
        ) {
          slug
          iconUrl
          displayName
        }
        ...ManagerTeamBadge_so5Leaderboard
        ...getLineupDisplayName_so5Leaderboard
      }
      so5Lineup {
        id
        hasLiveGames
        name
        so5Rankings {
          id
          ranking
          targetedRewardWithEligibility {
            reward {
              id
              target {
                slug
                pictureUrl
              }
            }
          }
          ...ManagerTeamBadge_so5Ranking
        }
        ...getLineupDisplayName_so5Lineup
      }
      managerTeam {
        id
        name
        ...ManagerTeamBadge_managerTeam
        ...ManagerTeamUpdateDialog_managerTeam
        ...getLineupDisplayName_managerTeam
      }
    }
    ${Badge.fragments.so5Leaderboard}
    ${getLineupDisplayName.fragments.so5Leaderboard}
    ${Badge.fragments.so5Ranking}
    ${getLineupDisplayName.fragments.so5Lineup}
    ${Badge.fragments.managerTeam}
    ${getLineupDisplayName.fragments.managerTeam}
    ${ManagerTeamUpdateDialog.fragments.managerTeam}
    ${isFixtureStarted.fragments.so5Fixture}
  ` as TypedDocumentNode<LobbyLineupHeader_so5LeaderboardContender>,
};
